import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { useClickOutside } from "../../hooks/useClickOutside";
import HistoryFilters from "./HistoryFilters";
import { LoadRequestHistoryItem, LoadRequestHistoryParams, LoadRequestHistoryResponse } from "./types";
import { getLoadRequestHistory } from "apis/myRequestsService";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import Pagination from "../../components/Pagination";
import SearchBar from "../Request/SearchBar";

// "16 September, 2026" — matches the date format used elsewhere in the app
// (e.g. FlightLIsting.tsx), instead of a locale-formatted "16/09/2026".
const formatHistoryDate = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = date.toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
  const year = date.getUTCFullYear();
  return `${day} ${month}, ${year}`;
};

interface RequestHistoryProps {
  onBack: () => void;
}

interface FilterState {
  requestedFromDate?: string;
  requestedToDate?: string;
  answeredFromDate?: string;
  answeredToDate?: string;
  status?: string;
}

const RequestHistory: React.FC<RequestHistoryProps> = ({ onBack }) => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [history, setHistory] = useState<LoadRequestHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [favourites, setFavourites] = useState<number[]>([]);
  const [filters, setFilters] = useState({
    requestedFromDate: "",
    requestedToDate: "",
    answeredFromDate: "",
    answeredToDate: "",
    status: "",
  });

  // The filters panel (and the calendars inside it) is a fixed-position
  // overlay with no backdrop, positioned over the list. <main> is the app
  // shell's only scroll container (see MainLayout.tsx), so without this the
  // list behind the panel keeps scrolling underneath it.
  useEffect(() => {
    const main = document.querySelector(".app-shell-scroll");
    if (!main) return;
    if (isFilterOpen) {
      main.classList.add("app-shell-scroll-locked");
    }
    return () => {
      main.classList.remove("app-shell-scroll-locked");
    };
  }, [isFilterOpen]);

  // Debounce search input to reduce unnecessary API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery); // Update only after debounce delay
    }, 500); // Adjust debounce delay if needed

    return () => clearTimeout(handler); // Cleanup previous timer
  }, [searchQuery]);

  // Reset to page 1 when search query or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters]);

  // Fetch history when page, rowsPerPage, search query, or filters change
  useEffect(() => {
    fetchHistory(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage, debouncedSearch, filters]);

  const fetchHistory = async (page: number, pageSize: number) => {
    setLoading(true);
    try {
      const params: LoadRequestHistoryParams = {
        page,
        pageSize,
        flightNameOrNumber: debouncedSearch.trim() || undefined, // Send search param if available
        ...filters, // Include filters in the API request
      };

      const data: LoadRequestHistoryResponse = await getLoadRequestHistory(params);
      setHistory(data.items);
      setTotalCount(data.totalCount);
      setTotalPages(Math.ceil(data.totalCount / pageSize));
    } catch (err) {
      setError("Failed to fetch history");
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  const handleApplyFilters = (newFilters: any) => {
    setFilters(newFilters);
    // The useEffect will automatically trigger a fetch with the new filters
  };

  const toggleFavourite = (id: number) => {
    setFavourites(
      (prev) =>
        prev.includes(id)
          ? prev.filter((item) => item !== id) // remove
          : [...prev, id] // add
    );
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  useClickOutside(
    dropdownRef,
    () => setIsFilterOpen(false),
    true,
    "mousedown",
    ".react-datepicker, .react-datepicker__portal, .st-status-menu"
  );

  return (
    <div className="">
      <section>
        <div className="bg-white rounded-lg p-3 sm:p-4 mt-4 mx-3 sm:mx-4">
          {/* Previous filter and search section remains the same */}
          <div className="flex flex-wrap gap-4 items-center justify-between pb-4">
            <div className="flex items-center gap-2">
              <nav className="flex flex-wrap items-center text-lg sm:text-xl font-bold font-manrope text-lightgray-200 gap-x-2 gap-y-1">
                <a
                  className="hover:text-royalblue-100 transition-colors duration-150 cursor-pointer"
                  onClick={onBack}
                >
                  Your Request
                </a>
                <i className="st-arrow-right text-lightgray-200 text-xs" aria-hidden="true"></i>
                <span className="text-black font-bold">History</span>
              </nav>
              {totalCount > 0 && (
                <span className="px-2.5 py-0.5 rounded-full bg-[#FBBF24] text-black text-xs font-bold">
                  {totalCount} {totalCount === 1 ? "record" : "records"}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 relative">
              <SearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
              <div className="relative">
                <button
                  id="dropdownDefaultButton-3"
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className="flex items-center gap-2 p-2.5 sm:p-3 bg-white border border-[#EDEEF1] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                >
                  <i className="st-filter text-[#000000]"></i>
                  <p className="font-medium text-[#000000] text-sm">Filters</p>
                </button>

                {isFilterOpen && (
                  <div
                    ref={dropdownRef}
                    className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:left-auto sm:translate-y-0 sm:mt-2 sm:w-96"
                  >
                    <HistoryFilters
                      initialFilters={filters}
                      closeDropdown={() => setIsFilterOpen(false)}
                      onApplyFilters={handleApplyFilters}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results (mobile cards + desktop table) with a loading overlay */}
          <div className="relative">
            {loading && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 rounded-lg">
                <div className="w-7 h-7 border-2 border-[#FFBC00] border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

          {/* Mobile card list (hidden on sm+) */}
          <div className="sm:hidden flex flex-col gap-3">
            {history.length === 0 ? (
              <p className="text-center text-sm text-gray-500 py-8">
                History not found
              </p>
            ) : (
              history.map((row, index) => {
                const statusKey = (row?.status || "").trim().toLowerCase();
                const statusColor =
                  statusKey === "expired"
                    ? "bg-red-100 text-red-600"
                    : statusKey === "answered"
                      ? "bg-green-100 text-green-600"
                      : statusKey === "pending"
                        ? "bg-[#FEF3C7] text-[#D97706]"
                        : statusKey === "update requested" ||
                            statusKey === "updated requested" ||
                            statusKey === "request updating"
                          ? "bg-[#FEF3C7] text-[#C2410C]"
                          : statusKey === "updated answer" ||
                              statusKey === "answer updated"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-600";
                return (
                  <div
                    key={index}
                    className="font-manrope bg-[#F8F8F8] border border-[#ECECED] rounded-[6px] p-3"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-[#1F2937] text-sm font-semibold truncate">
                          {row?.flightName || "—"}
                        </p>
                        <p className="text-[#9CA3AF] text-xs mt-0.5">
                          {row?.flightNumber || "—"}
                        </p>
                      </div>
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-md whitespace-nowrap ${statusColor}`}
                      >
                        {row?.status || "—"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-[#1F2937] text-sm font-bold">
                        {row?.departureAirport || "—"}
                      </p>
                      <i className="st-arrow-right text-[#9CA3AF] text-xs"></i>
                      <p className="text-[#1F2937] text-sm font-bold">
                        {row?.destinationAirport || "—"}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-[#9CA3AF]">Requested</p>
                        <p className="text-[#1F2937] mt-0.5">
                          {row?.requestedDate ? formatHistoryDate(row.requestedDate) : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[#9CA3AF]">Answered</p>
                        <p className="text-[#1F2937] mt-0.5">
                          {row?.status === "Expired" || !row?.answeredDate
                            ? "N/A"
                            : formatHistoryDate(row.answeredDate)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Table section (desktop only) */}
          <div className="relative overflow-x-auto sm:rounded-lg hidden sm:block">
            <table className="w-full text-sm text-left font-manrope">
              {/* Header matches the Open Request card palette: #F8F8F8 surface,
                  #ECECED borders, #737C8C label text. */}
              <thead className="text-xs bg-[#F8F8F8]">
                <tr>
                  <th scope="col" className="px-6 py-4 rounded-l-[6px]">
                    <p className="text-[#737C8C] font-semibold text-left">Sl.No.</p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l border-[#ECECED] text-[#737C8C] font-semibold text-left px-4">Flight Number</p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l border-[#ECECED] text-[#737C8C] font-semibold text-left px-4">Flight Name</p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l border-[#ECECED] text-[#737C8C] font-semibold text-left px-4">Requested Date</p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l border-[#ECECED] text-[#737C8C] font-semibold text-left px-4">Status</p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l border-[#ECECED] text-[#737C8C] font-semibold text-left px-4">Answered Date</p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l border-[#ECECED] text-[#737C8C] font-semibold text-left px-4">From</p>
                  </th>
                  <th scope="col" className="px-6 py-4 rounded-r-[6px]">
                    <p className="border-l border-[#ECECED] text-[#737C8C] font-semibold text-left px-4">To</p>
                  </th>
                </tr>
                <tr>
                  <td className="h-2 bg-white"></td>
                </tr>
              </thead>
              <tbody className="rounded-lg">
                {history.length > 0 &&
                  history?.map((row, index) => (
                    <tr key={index} className="odd:bg-white even:bg-[#F8F8F8] border border-[#ECECED]">
                      <td className="px-6 py-4 font-medium text-sm text-black text-left whitespace-nowrap">
                        <p>{(currentPage - 1) * rowsPerPage + index + 1}</p>
                      </td>
                      <td className="px-6 py-4 font-medium text-sm text-black text-left whitespace-nowrap">
                        <p className="px-4">{row?.flightNumber}</p>
                      </td>
                      <td className="px-6 py-4 font-medium text-sm text-black text-left whitespace-nowrap">
                        <p className="px-4">{row?.flightName}</p>
                      </td>
                      <td className="px-6 py-4 font-medium text-sm text-black text-left whitespace-nowrap">
                        <p className="px-4">{row?.requestedDate ? formatHistoryDate(row.requestedDate) : ""}</p>
                      </td>
                      <td className="px-6 py-4 font-medium text-sm text-black text-left whitespace-nowrap">
                        <p className="px-4">{row?.status}</p>
                      </td>
                      <td className="px-6 py-4 font-medium text-sm text-black text-left whitespace-nowrap">
                        <p className="px-4">{row.status === "Expired" ? "N/A" : formatHistoryDate(row.answeredDate)}</p>
                      </td>

                      <td className="px-6 py-4 font-medium text-sm text-black text-left whitespace-nowrap">
                        <p className="px-4">{row?.departureAirport}</p>
                      </td>
                      <td className="px-6 py-4 font-medium text-sm text-black text-left whitespace-nowrap">
                        <p className="px-4">{row?.destinationAirport}</p>
                      </td>
                      {/* <td className="px-6 py-4 text-center">
                        <button onClick={() => toggleFavourite(row.id)} className="p-2 rounded-full hover:bg-gray-100 transition">
                          {favourites.includes(row.id) ? (
                            <i className="st-heart text-red-500 text-lg"></i> // filled heart
                          ) : (
                            <i className="st-heart text-gray-400 text-lg"></i> // empty heart
                          )}
                        </button>
                      </td> */}
                    </tr>
                  ))}
                {history.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center">
                      History not found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </div>

          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={rowsPerPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handleRowsPerPageChange}
            containerClassName="flex flex-row flex-wrap items-center justify-between gap-3 mt-4"
          />
        </div>
      </section>
    </div>
  );
};

export default RequestHistory;
